/**
 * Two raw reads of media_ad_set_metrics: the latest date on record and how many
 * days a workspace actually covers. Used to decide whether a refit would see
 * anything new.
 */

import { getSupabaseClient, isSupabaseConfigured } from '@/config';
import { DEMO_MODE } from '@/config/demoConfig';
import { logError } from './logger';
import { scoped, type Scope } from './mediaIntelligenceShared';

/**
 * The most recent day this workspace has ad-platform metrics for, or null if it
 * has none at all.
 *
 * The rolling windows count back from today, so a workspace whose last sync
 * landed weeks ago silently reports near-zero rather than "your data stops
 * here". This is what the UI needs to tell those two apart. Google Analytics is
 * excluded because it keeps recording daily and would mask a stale ad sync.
 */
export const fetchLatestMetricDate = async (
  scope: Scope,
  opts: { strict?: boolean } = {},
): Promise<string | null> => {
  const supabase = getSupabaseClient();
  if (!supabase || DEMO_MODE) return null;

  const { data, error } = await scoped(
    supabase
      .from('media_ad_set_metrics')
      .select('date')
      .neq('platform', 'analytics')
      .order('date', { ascending: false })
      .limit(1),
    scope,
  );
  if (error) {
    logError('MediaIntelligenceService', 'Error fetching latest metric date', error);
    // Strict callers cannot tell "no data" from "could not read" through a null.
    if (opts.strict) throw new Error(error.message);
    return null;
  }
  const rows = (data ?? []) as Array<{ date: string | null }>;
  return rows[0]?.date ?? null;
};

/**
 * How many distinct days inside [start, end] actually carry ad-set metrics.
 *
 * get_campaign_summary cannot answer this: it is a per-campaign rollup, so its
 * start_date is a campaign attribute, not a day with data. Counting distinct
 * start_dates over 83 campaigns in a 30-day window yields 100% while the
 * workspace holds 9 days of metrics — which is why this reads the daily table
 * directly. Paged for the same reason every other batch read here is: PostgREST
 * truncates at PGRST_DB_MAX_ROWS and a short read would understate coverage.
 */
export const fetchMetricDaysCovered = async (
  scope: Scope,
  start: string,
  end: string,
): Promise<{ daysWithData: number; firstDate: string | null }> => {
  const supabase = getSupabaseClient();
  if (!isSupabaseConfigured()) return { daysWithData: 0, firstDate: null };

  const days = new Set<string>();
  const PAGE_SIZE = 1000;
  let from = 0;
  for (;;) {
    const { data, error } = await scoped(
      supabase
        .from('media_ad_set_metrics')
        .select('date, impressions, clicks, cost, conversions')
        // Google Analytics is excluded from the rollup this coverage describes,
        // so its days must not count here either. GA runs every day while the ad
        // platforms have gaps, so including it read 30% where the ad-platform
        // answer was 10%.
        .neq('platform', 'analytics')
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: true })
        // Tie-break on id: paging an ORDER BY with duplicate keys lets the
        // database order rows within a key differently per page, which drops
        // some rows at a page boundary and returns others twice. Measured on
        // this workspace: 3,942 rows fetched, 3,938 distinct.
        .order('id', { ascending: true })
        .range(from, from + PAGE_SIZE - 1),
      scope,
    );
    if (error) {
      logError('MediaIntelligenceService', 'Error counting metric days', error);
      // A partial walk is indistinguishable from a complete one, so it must not
      // be handed back as if it were. Every caller has a try/catch that turns
      // this into an error state rather than a plausible wrong number.
      throw new Error(`Failed counting metric days: ${(error as { message?: string })?.message ?? 'unknown error'}`);
    }
    const rows = (data ?? []) as Array<{
      date: string | null; impressions: number | null; clicks: number | null;
      cost: number | null; conversions: number | null;
    }>;
    // A day COUNTS when something was actually delivered on it. Row existence
    // is not data existence: sync writes a row per ad set per day whether or
    // not anything ran, so a dormant account manufactured coverage — 58 of the
    // 364 counted days carried nothing but zeros, inflating the /help bar from
    // 67% to 79% (round-2 audit).
    rows.forEach((r) => {
      if (!r.date) return;
      const delivered =
        (r.impressions || 0) > 0 || (r.clicks || 0) > 0 ||
        (r.cost || 0) > 0 || (r.conversions || 0) > 0;
      if (delivered) days.add(r.date);
    });
    if (rows.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  // Ascending order, so the first key inserted is the earliest day with data.
  // The 'all' window starts at a fixed sentinel decades before any campaign, and
  // measuring coverage against that reads 2% for a workspace with a near-complete
  // history. The caller uses this to measure from the data instead.
  const [firstDate] = [...days].sort();
  return { daysWithData: days.size, firstDate: firstDate ?? null };
};
